import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronDown, Search, Check, Flag, Tag, User,
} from 'lucide-react';
import { iniciales } from '../utils/helpers';
import { PriorityIcon } from '../utils/displays';
import { PRIORIDADES } from '../utils/constants';

// ----- Dropdown multi-select reusable -----

const FilterDropdown = ({ icon: Icon, label, selected = [], children, onClear }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const active = selected.length > 0;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-colors ${
          active
            ? 'bg-slate-900 text-white hover:bg-slate-800'
            : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
        }`}
      >
        {Icon && <Icon size={11} strokeWidth={2} />}
        <span>{label}</span>
        {active && (
          <span className="inline-flex items-center justify-center min-w-[14px] h-3.5 px-1 bg-white/20 rounded text-[9px] font-bold tabular-nums">
            {selected.length}
          </span>
        )}
        <ChevronDown size={10} className={`transition-transform ${open ? 'rotate-180' : ''} ${active ? 'text-white/80' : 'text-slate-300'}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.1 }}
            className="absolute left-0 top-full mt-1 w-56 bg-white rounded-lg shadow-xl ring-1 ring-slate-200 overflow-hidden z-20"
          >
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100">
              <span className="text-[10px] font-semibold tracking-widest text-slate-500 uppercase">
                Filtrar por {label.toLowerCase()}
              </span>
              {active && (
                <button
                  onClick={onClear}
                  className="text-[10px] text-slate-400 hover:text-rose-600"
                >
                  Limpiar
                </button>
              )}
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ----- Item de opcion -----

const FilterItem = ({ selected, onClick, children }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors ${
      selected ? 'bg-cyan-50' : 'hover:bg-slate-50'
    }`}
  >
    <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 transition-all ${
      selected ? 'bg-cyan-500 border-cyan-500' : 'border-slate-300'
    }`}>
      {selected && (
        <svg className="w-2 h-2 text-white" viewBox="0 0 12 12" fill="none">
          <path d="M2 6.5L4.5 9L10 3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
    <span className="flex-1 min-w-0 flex items-center gap-1.5">{children}</span>
  </button>
);

// ----- Componente principal -----

const BoardFilters = ({
  proyecto,
  filters,
  setFilters,
  userEmpleadoId,
}) => {
  const miembros = proyecto?.miembros || [];
  const etiquetas = proyecto?.etiquetas || [];

  const toggleInArray = (key, value) => {
    setFilters((f) => {
      const arr = f[key] || [];
      const next = arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
      return { ...f, [key]: next };
    });
  };

  const setArray = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
  };

  const totalActivo = (filters.asignados?.length || 0)
    + (filters.prioridades?.length || 0)
    + (filters.etiquetas?.length || 0)
    + (filters.soloMias ? 1 : 0)
    + (filters.busqueda ? 1 : 0);

  const clearAll = () => setFilters({ asignados: [], prioridades: [], etiquetas: [], soloMias: false, busqueda: '' });

  return (
    <div className="flex items-center gap-1.5 px-6 py-2 border-b border-slate-200/60 bg-white">
      {/* Buscador */}
      <div className="relative">
        <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={filters.busqueda || ''}
          onChange={(e) => setFilters((f) => ({ ...f, busqueda: e.target.value }))}
          placeholder="Buscar en tareas..."
          className="w-52 pl-7 pr-2 py-1 text-xs bg-slate-50 border border-transparent rounded-md placeholder-slate-400 focus:outline-none focus:bg-white focus:border-slate-200 transition-colors"
        />
        {filters.busqueda && (
          <button
            onClick={() => setFilters((f) => ({ ...f, busqueda: '' }))}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-700"
          >
            <X size={10} />
          </button>
        )}
      </div>

      <div className="w-px h-4 bg-slate-200 mx-1" />

      {/* Asignado */}
      <FilterDropdown
        icon={User}
        label="Asignado"
        selected={filters.asignados || []}
        onClear={() => setArray('asignados', [])}
      >
        {miembros.length === 0 ? (
          <div className="px-3 py-2 text-xs text-slate-400 italic">Sin miembros</div>
        ) : (
          <>
            <FilterItem
              selected={(filters.asignados || []).includes('null')}
              onClick={() => toggleInArray('asignados', 'null')}
            >
              <span className="w-5 h-5 rounded-full border border-dashed border-slate-300" />
              <span className="text-slate-500 italic text-xs">Sin asignar</span>
            </FilterItem>
            {miembros.map((m) => (
              <FilterItem
                key={m.empleado_id}
                selected={(filters.asignados || []).includes(m.empleado_id)}
                onClick={() => toggleInArray('asignados', m.empleado_id)}
              >
                <span
                  className="w-5 h-5 rounded-full text-[9px] font-semibold flex items-center justify-center"
                  style={{ backgroundColor: `${proyecto.color || '#06b6d4'}1f`, color: proyecto.color || '#06b6d4' }}
                >
                  {iniciales(m.nombre)}
                </span>
                <span className="truncate text-slate-800">{m.nombre}</span>
              </FilterItem>
            ))}
          </>
        )}
      </FilterDropdown>

      {/* Prioridad */}
      <FilterDropdown
        icon={Flag}
        label="Prioridad"
        selected={filters.prioridades || []}
        onClear={() => setArray('prioridades', [])}
      >
        {PRIORIDADES.map((p) => (
          <FilterItem
            key={p.key}
            selected={(filters.prioridades || []).includes(p.key)}
            onClick={() => toggleInArray('prioridades', p.key)}
          >
            <span className="w-3 h-3 inline-flex items-center justify-center flex-shrink-0">
              {p.key === 'ninguna'
                ? <span className="w-2 h-2 rounded-full border border-dashed border-slate-300" />
                : <PriorityIcon prioridad={p.key} />}
            </span>
            <span className="text-slate-800">{p.label}</span>
          </FilterItem>
        ))}
      </FilterDropdown>

      {/* Etiquetas */}
      {etiquetas.length > 0 && (
        <FilterDropdown
          icon={Tag}
          label="Etiquetas"
          selected={filters.etiquetas || []}
          onClear={() => setArray('etiquetas', [])}
        >
          {etiquetas.map((e) => (
            <FilterItem
              key={e.id}
              selected={(filters.etiquetas || []).includes(e.id)}
              onClick={() => toggleInArray('etiquetas', e.id)}
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: e.color || '#94a3b8' }}
              />
              <span className="text-slate-800">{e.nombre}</span>
            </FilterItem>
          ))}
        </FilterDropdown>
      )}

      {/* Toggle Mías (mismo lenguaje visual que los multi-selects: slate-900 cuando activo) */}
      {userEmpleadoId && (
        <button
          onClick={() => setFilters((f) => ({ ...f, soloMias: !f.soloMias }))}
          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-colors ${
            filters.soloMias
              ? 'bg-slate-900 text-white hover:bg-slate-800'
              : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          {filters.soloMias && <Check size={11} strokeWidth={2.5} />}
          Mías
        </button>
      )}

      {totalActivo > 0 && (
        <>
          <div className="w-px h-4 bg-slate-200 mx-1" />
          <button
            onClick={clearAll}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-rose-600 transition-colors"
          >
            <X size={11} />
            Limpiar
          </button>
        </>
      )}

      <div className="flex-1" />
    </div>
  );
};

// Helper exportado para aplicar filtros sobre un array de tareas
export const applyFilters = (tareas, filters, userEmpleadoId) => {
  if (!filters) return tareas;
  return (tareas || []).filter((t) => {
    if (filters.soloMias && !(t.asignados || []).some((a) => a.id === userEmpleadoId)) return false;
    if (filters.asignados?.length > 0) {
      const matchSinAsignar = filters.asignados.includes('null') && (t.asignados || []).length === 0;
      const matchAsignado = (t.asignados || []).some((a) => filters.asignados.includes(a.id));
      if (!matchSinAsignar && !matchAsignado) return false;
    }
    if (filters.prioridades?.length > 0 && !filters.prioridades.includes(t.prioridad || 'ninguna')) {
      return false;
    }
    if (filters.etiquetas?.length > 0) {
      const tareaEtIds = (t.etiquetas || []).map((e) => e.id);
      const hasMatch = filters.etiquetas.some((eid) => tareaEtIds.includes(eid));
      if (!hasMatch) return false;
    }
    if (filters.busqueda) {
      const q = filters.busqueda.trim().toLowerCase();
      if (q) {
        const codigo = (t.codigo || '').toLowerCase();
        const titulo = (t.titulo || '').toLowerCase();
        if (!codigo.includes(q) && !titulo.includes(q)) return false;
      }
    }
    return true;
  });
};

export default BoardFilters;
